<template>
  <div class="ride-billing-page">
    <Card>
      <div class="toolbar">
        <Button type="primary" @click="openCreate">新建计费策略</Button>
        <Button class="m-l-10" @click="load">刷新</Button>
      </div>
      <Table class="m-t-16" :loading="loading" :columns="columns" :data="list" border stripe />
    </Card>

    <Modal
      v-model="showForm"
      :title="form._id ? '编辑计费策略' : '新建计费策略'"
      width="720"
      :mask-closable="false"
      @on-cancel="resetForm"
    >
      <Form :label-width="110">
        <FormItem label="策略名称">
          <Input v-model="form.name" placeholder="如：城区默认载人计费" />
        </FormItem>
        <FormItem label="计费类型">
          <Select v-model="form.strategy_type" style="width: 240px" @on-change="onTypeChange">
            <Option value="hourly">按时间</Option>
            <Option value="tiered">分段计费</Option>
            <Option value="base_plus">起步价+超时</Option>
          </Select>
        </FormItem>

        <template v-if="form.strategy_type === 'hourly'">
          <FormItem label="计费单位">
            <Select v-model="form.config.rate_unit" style="width: 200px">
              <Option value="hour">按小时</Option>
              <Option value="minute">按分钟</Option>
            </Select>
          </FormItem>
          <FormItem :label="timeRateLabel">
            <InputNumber v-model="form.config.rate" :min="0.01" :step="0.1" style="width: 200px" />
          </FormItem>
        </template>

        <template v-if="form.strategy_type === 'base_plus'">
          <FormItem label="起步价(元)">
            <InputNumber v-model="form.config.base_fee_yuan" :min="0" :step="0.5" style="width: 200px" />
          </FormItem>
          <FormItem label="起步时长(分)">
            <InputNumber v-model="form.config.base_minutes" :min="0" style="width: 200px" />
          </FormItem>
          <FormItem label="超出(元/分)">
            <InputNumber v-model="form.config.extra_per_minute_yuan" :min="0" :step="0.1" style="width: 200px" />
          </FormItem>
        </template>

        <template v-if="form.strategy_type === 'tiered'">
          <FormItem label="分段规则">
            <div v-for="(tier, idx) in form.config.tiers" :key="idx" class="tier-row">
              <span>至</span>
              <InputNumber
                v-model="tier.up_to_minutes"
                :min="1"
                placeholder="分钟"
                style="width: 100px"
              />
              <span>分钟：</span>
              <Select v-model="tier.charge_type" style="width: 120px">
                <Option value="flat">一口价(元)</Option>
                <Option value="per_minute">每分钟(元)</Option>
              </Select>
              <InputNumber v-model="tier.amount_yuan" :min="0" :step="0.1" style="width: 100px" />
              <Button type="text" @click="removeTier(idx)" v-if="form.config.tiers.length > 1">删除</Button>
            </div>
            <Button size="small" @click="addTier">添加分段</Button>
            <div class="hint">最后一段可将「至」留空表示剩余时长</div>
          </FormItem>
        </template>

        <FormItem label="免费时长(分)">
          <InputNumber v-model="form.config.free_minutes" :min="0" style="width: 200px" />
        </FormItem>
        <FormItem label="最短计费(分)">
          <InputNumber v-model="form.config.min_minutes" :min="0" style="width: 200px" />
        </FormItem>
        <FormItem label="取整方式">
          <Select v-model="form.config.round_mode" style="width: 200px">
            <Option value="ceil">向上取整</Option>
            <Option value="round">四舍五入</Option>
            <Option value="floor">向下取整</Option>
          </Select>
        </FormItem>
        <FormItem label="单次封顶(元)">
          <Input v-model="form.config.cap_yuan" placeholder="留空表示不封顶" style="width: 200px" />
        </FormItem>

        <Divider orientation="left">适用范围</Divider>
        <FormItem label="适用区域">
          <Select v-model="form.area_ids" multiple filterable style="width: 100%">
            <Option v-for="a in areaOptions" :key="a.area_id" :value="a.area_id">
              {{ a.area_id }} - {{ a.area_name }}
            </Option>
          </Select>
          <div class="field-hint">按运营区域生效；单车计费请在「车辆管理」中绑定策略</div>
        </FormItem>
        <FormItem label="优先级">
          <InputNumber v-model="form.priority" style="width: 200px" />
          <span class="hint">数值越大越优先</span>
        </FormItem>
        <FormItem label="全局默认">
          <i-switch v-model="form.is_default" />
        </FormItem>
        <FormItem label="状态">
          <Select v-model="form.status" style="width: 200px">
            <Option value="on">启用</Option>
            <Option value="off">停用</Option>
          </Select>
        </FormItem>
        <FormItem label="展示文案">
          <Input v-model="form.display_summary" type="textarea" :rows="2" placeholder="留空则自动生成，用于小程序" />
        </FormItem>
        <FormItem label="备注">
          <Input v-model="form.remark" type="textarea" :rows="2" />
        </FormItem>
      </Form>
      <div slot="footer">
        <Button @click="showForm = false">取消</Button>
        <Button type="primary" :loading="saving" @click="saveForm">保存</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  rideBillingList,
  rideBillingCreate,
  rideBillingUpdate,
  rideBillingDelete,
} from '@/api/rideBilling'
import { getAreaList } from '@/api/area'

function defaultConfig(type) {
  if (type === 'tiered') {
    return {
      tiers: [
        { up_to_minutes: 15, charge_type: 'flat', amount_yuan: 2 },
        { up_to_minutes: null, charge_type: 'per_minute', amount_yuan: 0.5 },
      ],
      min_minutes: 1,
      round_mode: 'ceil',
      free_minutes: 0,
      cap_yuan: '',
    }
  }
  if (type === 'base_plus') {
    return {
      base_fee_yuan: 3,
      base_minutes: 30,
      extra_per_minute_yuan: 0.5,
      min_minutes: 1,
      round_mode: 'ceil',
      free_minutes: 0,
      cap_yuan: '',
    }
  }
  return {
    rate: 15,
    rate_unit: 'hour',
    hourly_rate: 15,
    min_minutes: 1,
    round_mode: 'ceil',
    free_minutes: 0,
    cap_yuan: '',
  }
}

function emptyForm() {
  return {
    _id: '',
    name: '',
    strategy_type: 'hourly',
    config: defaultConfig('hourly'),
    area_ids: [],
    priority: 0,
    is_default: false,
    status: 'on',
    display_summary: '',
    remark: '',
  }
}

export default {
  name: 'RideBillingManage',
  data() {
    return {
      loading: false,
      saving: false,
      showForm: false,
      list: [],
      areaOptions: [],
      form: emptyForm(),
      columns: [
        { title: '名称', key: 'name', minWidth: 140 },
        {
          title: '类型',
          key: 'strategy_type',
          width: 100,
          render: (h, { row }) => {
            const map = { hourly: '按时间', tiered: '分段', base_plus: '起步+超时' }
            return h('span', map[row.strategy_type] || row.strategy_type)
          },
        },
        { title: '规则摘要', key: 'display_summary', minWidth: 220 },
        {
          title: '区域',
          key: 'area_ids',
          width: 120,
          render: (h, { row }) =>
            h('span', row.area_ids && row.area_ids.length ? row.area_ids.join(',') : '—'),
        },
        { title: '优先级', key: 'priority', width: 80 },
        {
          title: '默认',
          key: 'is_default',
          width: 70,
          render: (h, { row }) => h('span', row.is_default ? '是' : '—'),
        },
        {
          title: '状态',
          key: 'status',
          width: 80,
          render: (h, { row }) =>
            h(
              'Tag',
              { props: { color: row.status === 'on' ? 'success' : 'default' } },
              row.status === 'on' ? '启用' : '停用'
            ),
        },
        {
          title: '操作',
          key: 'action',
          width: 160,
          align: 'center',
          render: (h, { row }) =>
            h('div', [
              h(
                'Button',
                {
                  props: { type: 'primary', size: 'small' },
                  style: { marginRight: '6px' },
                  on: { click: () => this.openEdit(row) },
                },
                '编辑'
              ),
              h(
                'Poptip',
                {
                  props: { confirm: true, title: '确定删除？' },
                  on: { 'on-ok': () => this.doDelete(row) },
                },
                [
                  h('Button', { props: { type: 'error', size: 'small' } }, '删除'),
                ]
              ),
            ]),
        },
      ],
    }
  },
  computed: {
    timeRateLabel() {
      return this.form.config && this.form.config.rate_unit === 'minute'
        ? '单价(元/分钟)'
        : '单价(元/小时)'
    },
  },
  mounted() {
    this.loadAreas()
    this.load()
  },
  methods: {
    loadAreas() {
      getAreaList({ pageNo: 1, pageSize: 500 }).then((res) => {
        if (res.data && res.data.code === 200 && res.data.data) {
          const rows = res.data.data
          this.areaOptions = Array.isArray(rows) ? rows : []
        }
      })
    },
    load() {
      this.loading = true
      rideBillingList()
        .then((res) => {
          if (res.data && res.data.code === 200) {
            this.list = res.data.data || []
          } else {
            this.$Message.error((res.data && res.data.msg) || '加载失败')
          }
        })
        .catch(() => {
          this.$Message.error('加载失败，请检查网络或重新登录')
        })
        .finally(() => {
          this.loading = false
        })
    },
    openCreate() {
      this.form = emptyForm()
      this.showForm = true
    },
    normalizeHourlyConfig(cfg) {
      const c = { ...cfg }
      if (c.rate == null && c.hourly_rate != null) c.rate = c.hourly_rate
      if (!c.rate_unit) c.rate_unit = 'hour'
      if (c.rate_unit === 'hour' && c.rate != null) c.hourly_rate = c.rate
      return c
    },
    openEdit(row) {
      this.form = {
        ...emptyForm(),
        ...JSON.parse(JSON.stringify(row)),
        config: { ...defaultConfig(row.strategy_type), ...(row.config || {}) },
      }
      if (this.form.strategy_type === 'hourly') {
        this.form.config = this.normalizeHourlyConfig(this.form.config)
      }
      if (this.form.config.cap_yuan == null) this.form.config.cap_yuan = ''
      this.showForm = true
    },
    onTypeChange(t) {
      this.form.config = defaultConfig(t)
    },
    addTier() {
      this.form.config.tiers.push({
        up_to_minutes: 30,
        charge_type: 'per_minute',
        amount_yuan: 0.5,
      })
    },
    removeTier(idx) {
      this.form.config.tiers.splice(idx, 1)
    },
    resetForm() {
      this.form = emptyForm()
    },
    buildPayload() {
      const cfg = { ...this.form.config }
      if (cfg.cap_yuan === '' || cfg.cap_yuan === undefined) cfg.cap_yuan = null
      else cfg.cap_yuan = Number(cfg.cap_yuan)
      if (this.form.strategy_type === 'hourly') {
        cfg.rate_unit = cfg.rate_unit === 'minute' ? 'minute' : 'hour'
        if (cfg.rate != null) cfg.hourly_rate = cfg.rate
      }
      if (this.form.strategy_type === 'tiered' && cfg.tiers) {
        cfg.tiers = cfg.tiers.map((t) => ({
          ...t,
          up_to_minutes:
            t.up_to_minutes === '' || t.up_to_minutes === undefined
              ? null
              : t.up_to_minutes,
        }))
      }
      return {
        name: this.form.name,
        strategy_type: this.form.strategy_type,
        config: cfg,
        area_ids: this.form.area_ids || [],
        priority: this.form.priority,
        is_default: this.form.is_default,
        status: this.form.status,
        display_summary: this.form.display_summary,
        remark: this.form.remark,
      }
    },
    saveForm() {
      if (!this.form.name || !this.form.name.trim()) {
        this.$Message.warning('请填写策略名称')
        return
      }
      this.saving = true
      const payload = this.buildPayload()
      const req = this.form._id
        ? rideBillingUpdate(this.form._id, payload)
        : rideBillingCreate(payload)
      req
        .then((res) => {
          if (res.data && res.data.code === 200) {
            this.$Message.success((res.data && res.data.msg) || '保存成功')
            this.showForm = false
            this.load()
          } else {
            this.$Message.error((res.data && res.data.msg) || '保存失败')
          }
        })
        .finally(() => {
          this.saving = false
        })
    },
    doDelete(row) {
      rideBillingDelete(row._id).then((res) => {
        if (res.data && res.data.code === 200) {
          this.$Message.success('已删除')
          this.load()
        } else {
          this.$Message.error((res.data && res.data.msg) || '删除失败')
        }
      })
    },
  },
}
</script>

<style scoped>
.ride-billing-page .toolbar {
  margin-bottom: 8px;
}
.m-l-10 {
  margin-left: 10px;
}
.m-t-16 {
  margin-top: 16px;
}
.hint {
  color: #999;
  font-size: 12px;
  margin-left: 8px;
}
.tier-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
</style>
